import { useState, useEffect } from 'react';
import { useUser } from '@/contexts/UserContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Activity, AlertTriangle, Download, Loader2, Filter, FileText, CheckCircle2, XCircle, Shield } from 'lucide-react';

interface AuditLog {
  id: string;
  eventType: string;
  entityType?: string;
  entityId?: string;
  timestamp: string;
  success: boolean;
  details: Record<string, any>;
}

interface AuditStats {
  totalEvents: number;
  eventsByType: Record<string, number>;
  failedEvents: number;
  lastActivity: string | null;
}

export function AuditoriaContent() {
  const { fetchWithAuth } = useUser();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterEventType, setFilterEventType] = useState<string>('');
  const [filterEntityType, setFilterEntityType] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [logsRes, statsRes] = await Promise.all([
        fetchWithAuth('/audit/logs?limit=100'),
        fetchWithAuth('/audit/stats')
      ]);

      if (!logsRes.ok || !statsRes.ok) {
        throw new Error('Error al cargar datos de auditoría');
      }

      const [logsData, statsData] = await Promise.all([
        logsRes.json(),
        statsRes.json()
      ]);

      setLogs(logsData.logs || []);
      setStats(statsData);
    } catch (error) {
      toast.error('Error al cargar auditoría', {
        description: error instanceof Error ? error.message : 'Error desconocido'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async (format: 'json' | 'csv' = 'json') => {
    try {
      const res = await fetchWithAuth(`/audit/export?format=${format}`);
      if (!res.ok) throw new Error('Error al exportar logs');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${Date.now()}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Logs exportados', {
        description: `Archivo ${format.toUpperCase()} descargado correctamente`
      });
    } catch (error) {
      toast.error('Error al exportar', {
        description: error instanceof Error ? error.message : 'Error desconocido'
      });
    }
  };

  const filteredLogs = logs.filter(log => {
    if (filterEventType && log.eventType !== filterEventType) return false;
    if (filterEntityType && log.entityType !== filterEntityType) return false;
    if (searchTerm && !log.eventType.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const getEventTypeBadge = (eventType: string): "default" | "destructive" | "outline" | "secondary" => {
    if (eventType.includes('CREADA') || eventType.includes('SUBIDO')) return 'default';
    if (eventType.includes('ELIMINADA') || eventType.includes('INTENTO')) return 'destructive';
    if (eventType.includes('ACTUALIZADA')) return 'secondary';
    if (eventType.includes('FIRMADA') || eventType.includes('VALIDACION')) return 'default';
    return 'outline';
  };

  const eventTypes = Array.from(new Set(logs.map(l => l.eventType)));
  const entityTypes = Array.from(new Set(logs.map(l => l.entityType).filter((t): t is string => !!t)));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con botones de exportación */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <p className="text-muted-foreground">
          Registro de actividad y eventos del sistema
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExport('csv')}>
            <Download className="mr-2 h-4 w-4" />
            Exportar CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('json')}>
            <Download className="mr-2 h-4 w-4" />
            Exportar JSON
          </Button>
        </div>
      </div>

      {/* Estadísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total de Eventos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-blue-600" />
                <span className="text-2xl font-bold">{stats.totalEvents}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Eventos Fallidos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <span className="text-2xl font-bold">{stats.failedEvents}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Tipos de Eventos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-green-600" />
                <span className="text-2xl font-bold">
                  {Object.keys(stats.eventsByType).length}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Última Actividad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-purple-600" />
                <span className="text-sm font-medium">
                  {stats.lastActivity
                    ? new Date(stats.lastActivity).toLocaleDateString('es-ES')
                    : 'N/A'}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Distribución de Eventos */}
      {stats && Object.keys(stats.eventsByType).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Distribución de Eventos</CardTitle>
            <CardDescription>
              Eventos más frecuentes en el sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(stats.eventsByType)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 10)
                .map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between p-2 hover:bg-muted/50 rounded">
                    <div className="flex items-center gap-2">
                      <Badge variant={getEventTypeBadge(type)} className="text-xs">
                        {type}
                      </Badge>
                    </div>
                    <span className="text-sm font-medium">{count}</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros de Búsqueda
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Buscar</label>
              <Input
                placeholder="Buscar por tipo de evento..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Tipo de Evento</label>
              <Select value={filterEventType || "all"} onValueChange={(value) => setFilterEventType(value === "all" ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos los eventos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los eventos</SelectItem>
                  {eventTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Tipo de Entidad</label>
              <Select value={filterEntityType || "all"} onValueChange={(value) => setFilterEntityType(value === "all" ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas las entidades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las entidades</SelectItem>
                  {entityTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {(filterEventType || filterEntityType || searchTerm) && (
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFilterEventType('');
                  setFilterEntityType('');
                  setSearchTerm('');
                }}
              >
                Limpiar Filtros
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabla de Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Registro de Actividad</CardTitle>
          <CardDescription>
            Mostrando {filteredLogs.length} de {logs.length} eventos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha/Hora</TableHead>
                <TableHead>Tipo de Evento</TableHead>
                <TableHead>Entidad</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Detalles</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                    No se encontraron eventos con los filtros aplicados
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm">
                      {new Date(log.timestamp).toLocaleString('es-ES', {
                        dateStyle: 'short',
                        timeStyle: 'short'
                      })}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getEventTypeBadge(log.eventType)}>
                        {log.eventType}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {log.entityType ? (
                        <span className="flex items-center gap-1">
                          <span className="text-muted-foreground">{log.entityType}</span>
                          {log.entityId && (
                            <code className="text-xs">#{log.entityId.substring(0, 8)}</code>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.success ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-600" />
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-xs truncate">
                      {JSON.stringify(log.details)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
